import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { REVALIDATE_MODES } from '@savgroup-front-common/constants';
import { countryList } from '@savgroup-front-common/core/domains/i18n/selectors';
import { BaseAutocompleteOptionForHookForm } from '@savgroup-front-common/types';

import OnboardingFormSchema from '../OnboardingForm.schema';

import useGetDomainNameFromCName from './useGetDomainNameFromCName';
import useUpsertSalesLeadCommand from './useUpsertSalesLeadCommand';

export interface OnboardingFormFieldValues {
  email: string;
  lastName: string;
  firstName: string;
  companyName: string;
  cName: string;
  phone: string;
  phoneCountryCode: string;
  cgv: boolean;
  address: string;
  postalCode: string;
  city: string;
  countryCode: BaseAutocompleteOptionForHookForm;
}

export interface HandleSelectAddressValues {
  countryCode?: string;
  streetNumber?: string;
  street?: string;
  city?: string;
  postalCode?: string;
}

const useOnboardingFormContext = ({
  handleUserEmail,
}: {
  handleUserEmail: (userEmail: string) => void;
}) => {
  const countryOptions = useSelector(countryList);

  const formContext = useForm<OnboardingFormFieldValues>({
    resolver: yupResolver(OnboardingFormSchema()),
    mode: REVALIDATE_MODES.ON_CHANGE,
    reValidateMode: REVALIDATE_MODES.ON_CHANGE,
  });

  const { handleSubmitSalesLead, isLoading } = useUpsertSalesLeadCommand();

  const { isDomainNameLoading, domain } = useGetDomainNameFromCName({
    formContext,
  });

  const { setValue } = formContext;

  const handleSelectAddress = useCallback(
    ({ countryCode, city, postalCode }: HandleSelectAddressValues = {}) => {
      setValue(
        'countryCode',
        countryOptions.filter((country) => country.value === countryCode)[0],
      );
      setValue('city', city || '');
      setValue('postalCode', postalCode || '');
    },
    [countryOptions, setValue],
  );

  const handleFormSubmit = useCallback(
    (data: OnboardingFormFieldValues) => {
      handleSubmitSalesLead(data);
      handleUserEmail(data.email);
    },
    [handleSubmitSalesLead, handleUserEmail],
  );

  return {
    formContext,
    countryOptions,
    handleFormSubmit,
    handleSelectAddress,
    isLoading,
    isDomainNameLoading,
    domain,
  };
};

export default useOnboardingFormContext;
